import { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ArrowCircleLeft, ArrowCircleRight } from "@mui/icons-material";
import { cloneDeep, isEqual, isNil } from "lodash";
import dayjs from "dayjs";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { styled } from "@mui/material/styles";
import enLocale from "dayjs/locale/en-au";
import DatePicker from "react-datepicker";
import "./styles.scss";
import Backdrop from "@mui/material/Backdrop";

import "react-datepicker/dist/react-datepicker.css";

enLocale.formats = {
  ...enLocale.formats,
  L: "",
};

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "dayIsBetween" && prop !== "isFirstDay" && prop !== "isLastDay",
})(({ theme, dayIsBetween, isFirstDay, isLastDay }) => ({
  ...(dayIsBetween && {
    borderRadius: 0,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    "&:hover, &:focus": {
      backgroundColor: theme.palette.primary.dark,
    },
  }),
  ...(isFirstDay && {
    borderTopLeftRadius: "50%",
    borderBottomLeftRadius: "50%",
  }),
  ...(isLastDay && {
    borderTopRightRadius: "50%",
    borderBottomRightRadius: "50%",
  }),
}));

const dateWrapper = () => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
    },
    "& .arrows": {
      cursor: "pointer",
      zIndex: 1000,
    },
    "& .disabledArrow": {
      pointerEvents: "none",
      opacity: "0.3",
    },
  };
};

const dateRangeWrapper = (theme) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& input": {
      fontSize: "14px",
      border: 0,
      maxWidth: "200px",
      textAlign: "center",
      color: theme.palette.primary.main,
    },
    "& input:focus-visible": {
      outline: "unset",
    },
    "& .week-input": {
      maxWidth: "100px !important",
    },
    "& .hidden-button": {
      position: "relative",
      "& button": {
        position: "absolute",
        width: "300px",
        left: "-150px",
        minHeight: "30px",
        borderRadius: "unset",
        "&:hover": {
          backgroundColor: "unset",
        },
        "& svg": {
          display: "none",
        },
        "& .MuiTouchRipple-root": {
          display: "none",
        },
      },
    },
  };
};

export const DateRangePicker = ({ onChange, window, value }) => {
  const [selectedDate, setSelectedDate] = useState([null, null]);
  const [view, setView] = useState(["day"]);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const [allowedPrevious, setAllowedPrevious] = useState(true);
  const customInputRef = useRef();

  useEffect(() => {
    if (isNil(selectedDate[0]) && isNil(selectedDate[1]) && value) {
      const date = cloneDeep(selectedDate);
      date[0] = dayjs(value?.start).isValid() ? value?.start : dayjs(new Date());
      date[1] = dayjs(value?.end).isValid() ? value?.end : dayjs(new Date());
      setSelectedDate(date);
    } else if (!isNil(selectedDate[0])) {
      if (!isEqual(dayjs(selectedDate[0]).format("YYYY-M-D"), value?.start)) {
        let date = cloneDeep(value);
        if (!Array.isArray(date)) {
          date = [];
        }
        date[0] = dayjs(value?.start).isValid() ? value?.start : dayjs(new Date());
        date[1] = dayjs(value?.end).isValid() ? value?.end : dayjs(new Date());
        setSelectedDate(date);
      }
    }
  }, [value]);

  useEffect(() => {
    if (!(isNil(selectedDate[0]) && isNil(selectedDate[1]))) {
      const date = {
        start: dayjs(selectedDate[0]).format("YYYY-M-D"),
        end: dayjs(selectedDate[1]).format("YYYY-M-D"),
      };
      onChange(date);

      checkPreviousDate();
    }
  }, [selectedDate]);

  useEffect(() => {
    checkPreviousDate();
    switch (window) {
      case "day":
        return setView(["day"]);
      case "week": {
        const start = dayjs(value?.start).startOf("week").add(1, "day");
        const end = dayjs(value?.start).endOf("week").add(1, "day");
        const date = cloneDeep(selectedDate);
        date[0] = start;
        date[1] = end;
        setSelectedDate(date);
        return setView(["day"]);
      }

      case "month":
        return setView(["month"]);
      case "year":
        return setView(["year"]);
      default:
        return setView(["day"]);
    }
  }, [window]);

  const checkPreviousDate = () => {
    if (!isNil(selectedDate[0])) {
      const prev = dayjs(selectedDate[0]).subtract(1, window);
      setAllowedPrevious(!(prev.get("year") < 2022));
    }
  };

  const onChangeDateRange = (v) => {
    if (isNil(v)) return;
    let newValue = v;
    if (window === "day") {
      newValue[0] = v;
      newValue[1] = v;
    } else {
      newValue[0] = dayjs(v).startOf(window);
      newValue[1] = dayjs(v).endOf(window);
    }
    setSelectedDate(newValue);
  };

  const onClickArrow = (count) => {
    const newValue = cloneDeep(selectedDate);
    if (!isNil(newValue[0]) && !isNil(newValue[0])) {
      newValue[0] = dayjs(newValue[0]).add(count, window);
      newValue[1] = dayjs(newValue[1]).add(count, window);
      setSelectedDate(newValue);
    }
  };

  const getDateValue = () => {
    if (window === "day") {
      return dayjs(selectedDate[0]).format("dddd D MMM YYYY");
    } else if (window === "week") {
      return dayjs(selectedDate[0]).format("D MMM YYYY");
    } else if (window === "month") {
      return dayjs(selectedDate[0]).format("MMMM YYYY");
    } else {
      return dayjs(selectedDate[0]).format("YYYY");
    }
  };

  const getWeekToValue = () => {
    return dayjs(selectedDate[1]).format("D MMM YYYY");
  };

  const renderWeekPickerDay = (date, selectedDates, pickersDayProps) => {
    if (!selectedDate[0]) {
      return <PickersDay {...pickersDayProps} />;
    }
    let start = dayjs(selectedDate[0]).startOf("week");
    let end = dayjs(selectedDate[0]).endOf("week");

    if (dayjs(selectedDate[0]).startOf("week").get("day") === 0) {
      start = dayjs(selectedDate[0]).startOf("week").add(1, "day");
      end = dayjs(selectedDate[0]).endOf("week").add(1, "day");
    }

    const dayIsBetween = date.isBetween(start, end, null, "[]");
    const isFirstDay = date.isSame(start, "day");
    const isLastDay = date.isSame(end, "day");

    return (
      <CustomPickersDay
        {...pickersDayProps}
        dayIsBetween={dayIsBetween}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
      />
    );
  };

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={openBackdrop}
        onClick={() => setOpenBackdrop(false)}
      ></Backdrop>
      {window !== "month" ? (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-au"}>
          <MuiDatePicker
            views={view}
            value={selectedDate.length > 0 ? selectedDate[0] : selectedDate}
            closeOnSelect={true}
            disableFuture={true}
            PopperProps={{ anchorEl: customInputRef.current }}
            showDaysOutsideCurrentMonth={true}
            minDate="01-01-2022"
            onChange={onChangeDateRange}
            renderDay={window === "week" ? renderWeekPickerDay : null}
            renderInput={(params) => (
              <Box sx={dateWrapper()} mb={1}>
                <ArrowCircleLeft
                  className={`arrows ${allowedPrevious ? "" : "disabledArrow"}`}
                  onClick={() => onClickArrow(-1)}
                />
                {isNil(selectedDate[0]) ? (
                  <Box sx={(theme) => dateRangeWrapper(theme)}>
                    <input
                      {...params.inputProps}
                      ref={params.inputRef}
                      value="Select Dates"
                    />
                    <Box className="hidden-button">
                      {params?.InputProps?.endAdornment}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={(theme) => dateRangeWrapper(theme)}>
                    <input
                      {...params.inputProps}
                      value={getDateValue()}
                      placeholder="Select from date"
                      className={`${window === "week" ? "week-input" : ""}`}
                      ref={customInputRef}
                    />
                    <Box className="hidden-button">
                      {params?.InputProps?.endAdornment}
                    </Box>
                    {window === "week" && (
                      <>
                        <span className="spanner">-</span>
                        <input
                          {...params.inputProps}
                          ref={params.inputRef}
                          value={getWeekToValue()}
                          placeholder="Select to date"
                          className={`${window === "week" ? "week-input" : ""}`}
                        />
                      </>
                    )}
                  </Box>
                )}
                <ArrowCircleRight
                  className="arrows"
                  onClick={() => onClickArrow(1)}
                />
              </Box>
            )}
          />
        </LocalizationProvider>
      ) : (
        <Box sx={dateWrapper()} mb={1}>
          <ArrowCircleLeft
            className={`arrows ${allowedPrevious ? "" : "disabledArrow"}`}
            onClick={() => onClickArrow(-1)}
          />
          {isNil(selectedDate[0]) ? (
            <Box sx={(theme) => dateRangeWrapper(theme)}>
              <input value="Select Dates" />
            </Box>
          ) : (
            <Box sx={(theme) => dateRangeWrapper(theme)}>
              <DatePicker
                selected={selectedDate[0] ? new Date(dayjs(selectedDate[0])) : null}
                onChange={(value) => onChangeDateRange(dayjs(value))}
                dateFormat="MMMM yyyy"
                showMonthYearPicker
                showPopperArrow={false}
                onCalendarClose={() => setOpenBackdrop(false)}
                onCalendarOpen={() => setOpenBackdrop(true)}
                popperClassName="ee-monthpicker"
                popperModifiers={[
                  {
                    name: "offset",
                    options: {
                      offset: [-80, 0],
                    },
                  },
                  {
                    name: "preventOverflow",
                    options: {
                      rootBoundary: "viewport",
                      tether: false,
                      altAxis: true,
                    },
                  },
                ]}
              />
            </Box>
          )}
          <ArrowCircleRight className="arrows" onClick={() => onClickArrow(1)} />
        </Box>
      )}
    </>
  );
};

export default DateRangePicker;
